import DOMPurify from 'isomorphic-dompurify';

// SI widgets are delivered by the CMS as a trusted custom-element interface.
// Keep the element and only the configuration attributes used by the SI bundle;
// do not allow arbitrary custom elements or arbitrary attributes through the
// general CMS HTML sanitiser.
// The second alternation group (tournament-id ... transparent) restores
// configuration attributes the CMS genuinely emits on TournamentTable and
// other widgets (verified against the raw /pages/{slug} API response). They
// were being stripped, which silently degraded those widgets — e.g. a
// TournamentTable without tournament-id/season-id falls back to the widget's
// own defaults instead of the table the editor pinned. All are plain
// configuration values: non-executable, no URLs, no script surface.
const siWidgetAttribute = /^(?:widget-id|width|height|show-bench|provider|event-id|active-im-editor|card|language|acid|abt-id|limit|across-all-tournaments|side|type|tournament-id|season-id|selected-team-ids|form|initial-collapse|nopadding|transparent)$/i;

/**
 * Sanitise a CMS HTML string before rendering via dangerouslySetInnerHTML.
 * Strips script elements, event handler attributes (onerror, onload, etc.),
 * and javascript: URIs. Safe to call on both server and client.
 */
export function sanitiseHtml(html: string | null | undefined): string {
    if (!html) return '';
    // ALLOW_DATA_ATTR preserves data-* attributes used by third-party widgets
    // (e.g. SI widget <figure data-home="..." data-away="...">). data-* attributes
    // are non-executable (they cannot run scripts or navigate URLs), but they are
    // still untrusted CMS input — any script reading element.dataset.* should
    // treat those values as untrusted at the point of consumption.
    //
    // svg: true is required alongside html: true so that SVG elements embedded
    // inside HTML (e.g. <figure><svg>…</svg></figure>, or <svg> used directly as
    // a widget placeholder by SI bundle) are preserved. Without it DOMPurify strips
    // all <svg> and child elements, preventing those widget types from initialising.
    // XSS vectors are still blocked: <foreignObject> is stripped, javascript: URIs
    // and event-handler attributes (onload, onerror, etc.) are removed.
    return DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true, svg: true },
        ALLOW_DATA_ATTR: true,
        CUSTOM_ELEMENT_HANDLING: {
            tagNameCheck: /^si-lb-widget$/i,
            attributeNameCheck: (attributeName, tagName) =>
                tagName?.toLowerCase() === 'si-lb-widget' && siWidgetAttribute.test(attributeName),
            allowCustomizedBuiltInElements: false,
        },
    });
}

/**
 * Sanitise a CMS-supplied SVG string before rendering via dangerouslySetInnerHTML.
 * Uses the SVG profile which strips script elements and event handler attributes
 * while preserving valid SVG markup.
 */
export function sanitiseSvg(svg: string | null | undefined): string {
    if (!svg) return '';
    return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });
}
